import React from 'react';
import { NavLink, Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom';
import { Divider, Label, Menu } from 'semantic-ui-react';

import { clienteApi } from 'src/api';
import { Contexto } from 'src/contexto';
import Compromisos, { TipoCompromiso, TipoNuevoCompromiso } from './Compromisos';
import InformacionApoderado from './InformacionApoderado';
import MasOpciones from './MasOpciones';
import Observaciones from './Observaciones';
import PagosEventuales from './PagosEventuales';
import PlanesDePago from './PlanesDePago';
import { Apoderado, PagoEventual } from './tipos';

interface State {
    buscando: boolean;
    datos?: Apoderado;
    pagosEventuales?: ReadonlyArray<PagoEventual>;
}

class FichaDeApoderado extends React.Component<RouteComponentProps<{ id: string }>, State> {
    public static readonly contextType = Contexto;
    public readonly context!: React.ContextType<typeof Contexto>;

    public state: State = {
        buscando: true,
    };

    public componentDidMount() {
        this.actualizarDatos();
    }

    public render() {
        const { buscando, datos } = this.state;

        if (buscando) {
            return <h1>Cargando...</h1>;
        }

        if (datos === undefined) {
            return <h1>No se encontraron los datos solicitados</h1>;
        }

        const {
            nombreCompleto: nombreCompletoApoderado,
            rutPasaporte: rutPasaporteApoderado,
            correos: correoApoderado,
            direcciones,
            telefonos,
            planesDePago,
        } = datos;
        const planDePagoVigente = planesDePago[0];
        const compromisos = planDePagoVigente.observacionesYCompromisos.filter(
            x => x.tipo === 'compromiso',
        );
        const observaciones = planDePagoVigente.observacionesYCompromisos.filter(
            x => x.tipo !== 'compromiso',
        );
        const pagosEventuales = this.state.pagosEventuales || [];

        const rutaBase = this.props.match.url;

        return (
            <div>
                <InformacionApoderado
                    nombreCompletoApoderado={nombreCompletoApoderado}
                    rutPasaporte={rutPasaporteApoderado}
                    correo={correoApoderado}
                    telefonos={telefonos}
                    direcciones={direcciones}
                />

                <Divider hidden />

                <Menu tabular>
                    <Menu.Item as={NavLink} exact to={rutaBase + '/planes'}>
                        Planes de pago
                    </Menu.Item>

                    <Menu.Item as={NavLink} exact to={rutaBase + '/compromisos'}>
                        Compromisos
                        {compromisos.length > 0 && (
                            <Label size='small' color='teal'>
                                {compromisos.length}
                            </Label>
                        )}
                    </Menu.Item>

                    <Menu.Item as={NavLink} exact to={rutaBase + '/observaciones'}>
                        Observaciones
                        {observaciones.length > 0 && (
                            <Label size='small' color='teal'>
                                {observaciones.length}
                            </Label>
                        )}
                    </Menu.Item>

                    <Menu.Item as={NavLink} exact to={rutaBase + '/pagoseventuales'}>
                        Otros pagos
                        {pagosEventuales.length > 0 && (
                            <Label size='small' color='teal'>
                                {pagosEventuales.length}
                            </Label>
                        )}
                    </Menu.Item>

                    <Menu.Item as={NavLink} exact to={rutaBase + '/mas-opciones'}>
                        Más opciones
                    </Menu.Item>
                </Menu>

                <Switch>
                    <Route
                        exact
                        path={rutaBase + '/planes'}
                        render={() => (
                            <PlanesDePago
                                {...planDePagoVigente}
                                nombreCompletoApoderado={nombreCompletoApoderado}
                                rutPasaporte={rutPasaporteApoderado}
                                alRealizarPago={this.actualizarDatos}
                                alEditarPago={this.actualizarDatos}
                            />
                        )}
                    />
                    <Route
                        exact
                        path={rutaBase + '/compromisos'}
                        render={() => (
                            <Compromisos
                                compromisos={compromisos}
                                alRegistrarCompromiso={async compromiso =>
                                    this.registrarCompromiso(
                                        compromiso,
                                        planDePagoVigente.idPlanDePago,
                                    )
                                }
                                alEliminarCompromiso={compromiso => {
                                    this.eliminarCompromiso(
                                        compromiso,
                                        planDePagoVigente.idPlanDePago,
                                    );
                                }}
                            />
                        )}
                    />
                    <Route
                        exact
                        path={rutaBase + '/observaciones'}
                        render={() => (
                            <Observaciones
                                observaciones={observaciones}
                                alRegistrarObservacion={async observacion =>
                                    this.registrarObservacion(
                                        observacion,
                                        planDePagoVigente.idPlanDePago,
                                    )
                                }
                                alEliminarObservacion={observacion => {
                                    this.eliminarObservacion(
                                        observacion,
                                        planDePagoVigente.idPlanDePago,
                                    );
                                }}
                            />
                        )}
                    />
                    <Route
                        exact
                        path={rutaBase + '/pagoseventuales'}
                        render={() => (
                            <PagosEventuales
                                datos={pagosEventuales}
                                idPlanDePago={datos.planesDePago[0].idPlanDePago}
                                nombreCompletoApoderado={nombreCompletoApoderado}
                                rutPasaporte={rutPasaporteApoderado}
                                alModificarPagos={this.actualizarDatos}
                            />
                        )}
                    />
                    <Route
                        exact
                        path={rutaBase + '/mas-opciones'}
                        render={({ history }) => (
                            <MasOpciones idApoderado={datos.idApoderado} historial={history} />
                        )}
                    />
                    <Redirect exact from={rutaBase} to={rutaBase + '/planes'} />
                </Switch>
            </div>
        );
    }

    private readonly actualizarDatos = () => {
        clienteApi
            .ejecutar('obtenerPlanesDePago', { idApoderado: this.props.match.params.id })
            .then(async apoderado => {
                if (apoderado) {
                    this.setState({ datos: apoderado });
                } else {
                    this.setState({ datos: undefined });
                }

                if (apoderado) {
                    const { idPlanDePago } = apoderado.planesDePago[0];
                    const { pagosEventuales } = await clienteApi.ejecutar(
                        'obtenerPagosEventuales',
                        { idPlanDePago },
                    );

                    this.setState({ pagosEventuales });
                } else {
                    this.setState({ pagosEventuales: undefined });
                }

                this.setState({ buscando: false });
            })
            .catch(error => {
                this.setState({ datos: undefined, buscando: false });
                this.context.mostrarModalError(error);
            });
    };

    private readonly registrarCompromiso = async (compromiso: TipoNuevoCompromiso, id: string) => {
        try {
            await clienteApi.ejecutar('registrarObservacionOCompromisoEnPlanDePago', {
                idPlanDePago: id,
                ...compromiso,
            });
            this.actualizarDatos();
        } catch (error) {
            this.context.mostrarModalError(error);
        }
    };

    private readonly eliminarCompromiso = (compromiso: TipoCompromiso, id: string) => {
        clienteApi
            .ejecutar('eliminarObservacionOCompromisoDePlanDePago', {
                idPlanDePago: id,
                ...compromiso,
            })
            .then(() => this.actualizarDatos())
            .catch(this.context.mostrarModalError);
    };

    private readonly registrarObservacion = async (obs: TipoNuevoCompromiso, id: string) => {
        return this.registrarCompromiso(obs, id);
    };
    private readonly eliminarObservacion = (obs: TipoCompromiso, id: string) => {
        this.eliminarCompromiso(obs, id);
    };
}

export default FichaDeApoderado;



// WEBPACK FOOTER //
// ./src/modulos/Cobranzas/componentes/FichaDeApoderado/index.tsx